import { useMemo } from 'react';
import { Skeleton } from '@rocco/components/skeleton';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../../types';
import { useChat } from '../../hooks/use-chat';
import {
    getChatParticipants,
    getChatTitle,
    getPreviewMessage,
} from '../../utils';
import { ChatInboxListItemAvatar } from './ChatInboxListItemAvatar';
import { NotificationBadge } from '../NotificationBadge';

/**
 * A conversation item in the chat inbox list.
 * @see {@link ChatInboxList}
 */
export function ChatInboxListItem({
    conversation,
}: {
    conversation: Conversation;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { openChatBox } = useChat();
    const otherParticipants = useMemo(() => {
        return getChatParticipants(conversation, isLoggedIn ? user.ID : 0);
    }, [conversation, isLoggedIn, user.ID]);
    const messagePreview = useMemo(() => {
        return getPreviewMessage(conversation.Messages.nodes[0], user.ID);
    }, [user.ID, conversation]);
    const unreadCount = useMemo(() => {
        return conversation.UnreadMessageCount || Number(conversation.Unread);
    }, [conversation.UnreadMessageCount, conversation.Unread]);

    return (
        <li
            className="flex items-center gap-3 rounded-lg bg-white/40 p-3 text-black hover:bg-white/70"
            role="button"
            aria-label="Open conversation"
            onClick={() => openChatBox(Number(conversation.ID))}
        >
            <ChatInboxListItemAvatar conversation={conversation} />
            <div className="min-w-0 flex-1 space-y-1 leading-tight">
                <div className="truncate text-4 font-medium">
                    {getChatTitle(conversation, otherParticipants)}
                </div>
                <div className="truncate text-3.5 text-black/80">
                    {messagePreview}
                </div>
            </div>
            <NotificationBadge count={unreadCount} preserveSpace />
        </li>
    );
}

/**
 * The loading skeleton for the chat inbox list item.
 * @see {@link ChatInboxListItem}
 */
export function ChatInboxListItemSkeleton() {
    return (
        <li className="flex items-center gap-3 rounded-lg bg-white/40 p-3">
            <Skeleton className="size-12 rounded-full bg-gray-500" />
            <div className="min-w-0 flex-1 space-y-1">
                <Skeleton className="h-5 w-1/3 bg-gray-500" />
                <Skeleton className="h-4 w-2/3 bg-gray-500" />
            </div>
        </li>
    );
}
